import React from 'react';
import ArrowForwardRoundedIcon from '@material-ui/icons/ArrowForwardRounded';
import { useLanguage } from '../contexts/LanguageContext';

const COPY = {
  en: {
    eyebrow: 'About us',
    title: 'Built for the people moving global trade.',
    intro: 'Fleet Intellistream Pte. Ltd. is a Singapore-based maritime technology company focused on making complex shipping work clearer, faster, and more connected.',
    detail: 'Our FleetGO AI Workbench brings voyage estimation, operations, compliance, and finance into one intelligent workspace—so shipping teams can spend less time entering data and more time making decisions.',
    marker: 'Singapore · Maritime technology',
    product: 'FleetGO AI Workbench',
    pillars: [
      { number: '01', title: 'Maritime by design', body: 'Purpose-built around the workflows, constraints, and realities of commercial shipping.' },
      { number: '02', title: 'Decisions over data entry', body: 'AI handles repetitive work and turns operational information into timely, usable context.' },
      { number: '03', title: 'One connected workspace', body: 'Commercial, operational, compliance, and financial work stays aligned from one voyage to the next.' },
    ],
    cta: 'Talk to our team',
  },
  zh: {
    eyebrow: '关于我们',
    title: '为推动全球贸易的航运人而打造。',
    intro: 'Fleet Intellistream Pte. Ltd. 是一家总部位于新加坡的海事科技公司，致力于让复杂的航运工作更清晰、更高效、更协同。',
    detail: 'FleetGO AI Workbench 将航次估算、运营、合规和财务整合至同一个智能工作空间，让航运团队减少数据录入，把更多时间用于决策。',
    marker: '新加坡 · 海事科技',
    product: 'FleetGO AI Workbench',
    pillars: [
      { number: '01', title: '专为海运而生', body: '围绕商业航运的实际流程、约束和业务场景进行设计。' },
      { number: '02', title: '聚焦决策，而非录入', body: '利用 AI 处理重复工作，将运营信息转化为及时、可用的决策依据。' },
      { number: '03', title: '统一协作空间', body: '让商务、运营、合规与财务工作在每个航次中保持协同。' },
    ],
    cta: '联系我们',
  },
  'zh-TW': {
    eyebrow: '關於我們',
    title: '為推動全球貿易的航運人而打造。',
    intro: 'Fleet Intellistream Pte. Ltd. 是一家總部位於新加坡的海事科技公司，致力於讓複雜的航運工作更清晰、更高效、更協同。',
    detail: 'FleetGO AI Workbench 將航次估算、營運、合規和財務整合至同一個智慧工作空間，讓航運團隊減少資料輸入，把更多時間用於決策。',
    marker: '新加坡 · 海事科技',
    product: 'FleetGO AI Workbench',
    pillars: [
      { number: '01', title: '專為海運而生', body: '圍繞商業航運的實際流程、限制和業務場景進行設計。' },
      { number: '02', title: '聚焦決策，而非輸入', body: '利用 AI 處理重複工作，將營運資訊轉化為及時、可用的決策依據。' },
      { number: '03', title: '統一協作空間', body: '讓商務、營運、合規與財務工作在每個航次中保持協同。' },
    ],
    cta: '聯絡我們',
  },
  fr: {
    eyebrow: 'À propos',
    title: 'Conçu pour celles et ceux qui font avancer le commerce mondial.',
    intro: 'Fleet Intellistream Pte. Ltd. est une entreprise singapourienne de technologie maritime qui rend les opérations de transport maritime complexes plus claires, rapides et connectées.',
    detail: "FleetGO AI Workbench réunit l'estimation des voyages, les opérations, la conformité et la finance dans un espace intelligent afin que les équipes se concentrent sur les décisions plutôt que sur la saisie.",
    marker: 'Singapour · Technologie maritime',
    product: 'FleetGO AI Workbench',
    pillars: [
      { number: '01', title: 'Pensé pour le maritime', body: 'Conçu autour des flux, des contraintes et des réalités du transport maritime commercial.' },
      { number: '02', title: 'Décider plutôt que saisir', body: "L'IA traite les tâches répétitives et transforme les données opérationnelles en contexte exploitable." },
      { number: '03', title: 'Un espace connecté', body: 'Les activités commerciales, opérationnelles, réglementaires et financières restent alignées.' },
    ],
    cta: 'Parler à notre équipe',
  },
  de: {
    eyebrow: 'Über uns',
    title: 'Für die Menschen, die den Welthandel bewegen.',
    intro: 'Fleet Intellistream Pte. Ltd. ist ein maritimes Technologieunternehmen aus Singapur, das komplexe Schifffahrtsarbeit klarer, schneller und vernetzter macht.',
    detail: 'FleetGO AI Workbench vereint Reisekalkulation, Betrieb, Compliance und Finanzen in einem intelligenten Arbeitsbereich – damit Teams weniger Daten erfassen und mehr entscheiden.',
    marker: 'Singapur · Maritime Technologie',
    product: 'FleetGO AI Workbench',
    pillars: [
      { number: '01', title: 'Für die Schifffahrt entwickelt', body: 'Konzipiert für die Abläufe, Anforderungen und Realitäten der kommerziellen Schifffahrt.' },
      { number: '02', title: 'Entscheiden statt eingeben', body: 'KI übernimmt wiederkehrende Arbeit und macht Betriebsinformationen direkt nutzbar.' },
      { number: '03', title: 'Ein vernetzter Arbeitsbereich', body: 'Kommerzielle, operative, regulatorische und finanzielle Arbeit bleibt durchgängig abgestimmt.' },
    ],
    cta: 'Mit unserem Team sprechen',
  },
  ja: {
    eyebrow: '私たちについて',
    title: '世界の物流を動かす人々のために。',
    intro: 'Fleet Intellistream Pte. Ltd. はシンガポールを拠点とする海事テクノロジー企業です。複雑な海運業務を、より明確に、速く、つながるものへ変えていきます。',
    detail: 'FleetGO AI Workbench は、航海見積、運航、コンプライアンス、財務をひとつのインテリジェントな環境に統合し、入力作業ではなく意思決定に集中できるよう支援します。',
    marker: 'シンガポール · 海事テクノロジー',
    product: 'FleetGO AI Workbench',
    pillars: [
      { number: '01', title: '海運のための設計', body: '商業海運のワークフロー、制約、現場の実情を起点に設計しています。' },
      { number: '02', title: '入力より意思決定', body: 'AI が反復作業を担い、運航情報をタイムリーで実用的な判断材料に変えます。' },
      { number: '03', title: 'つながるワークスペース', body: '営業、運航、コンプライアンス、財務の業務を航海ごとに一貫して連携します。' },
    ],
    cta: 'チームに相談する',
  },
  ko: {
    eyebrow: '회사 소개',
    title: '세계 무역을 움직이는 사람들을 위해 만듭니다.',
    intro: 'Fleet Intellistream Pte. Ltd.는 싱가포르에 기반을 둔 해양 기술 기업으로, 복잡한 해운 업무를 더 명확하고 빠르며 유기적으로 만드는 데 집중합니다.',
    detail: 'FleetGO AI Workbench는 항차 견적, 운영, 규정 준수, 재무를 하나의 지능형 작업 공간으로 연결하여 해운 팀이 입력보다 의사결정에 집중하도록 돕습니다.',
    marker: '싱가포르 · 해양 기술',
    product: 'FleetGO AI Workbench',
    pillars: [
      { number: '01', title: '해운을 위한 설계', body: '상업 해운의 실제 업무 흐름, 제약 조건, 현장 환경을 중심으로 설계했습니다.' },
      { number: '02', title: '입력보다 의사결정', body: 'AI가 반복 업무를 처리하고 운영 정보를 적시에 활용할 수 있는 맥락으로 바꿉니다.' },
      { number: '03', title: '하나로 연결된 작업 공간', body: '영업, 운영, 규정 준수, 재무 업무가 항차 전반에서 일관되게 연결됩니다.' },
    ],
    cta: '팀에 문의하기',
  },
  ar: {
    eyebrow: 'من نحن',
    title: 'صُمم للأشخاص الذين يحرّكون التجارة العالمية.',
    intro: 'Fleet Intellistream Pte. Ltd. شركة سنغافورية للتقنيات البحرية، تركّز على جعل أعمال الشحن المعقدة أوضح وأسرع وأكثر ترابطاً.',
    detail: 'تجمع FleetGO AI Workbench تقدير الرحلات والعمليات والامتثال والشؤون المالية في مساحة عمل ذكية واحدة، ليقضي فريق الشحن وقتاً أقل في إدخال البيانات ووقتاً أكبر في اتخاذ القرار.',
    marker: 'سنغافورة · التقنيات البحرية',
    product: 'FleetGO AI Workbench',
    pillars: [
      { number: '01', title: 'مصمم للقطاع البحري', body: 'بُني حول مسارات العمل والقيود والواقع الفعلي للشحن التجاري.' },
      { number: '02', title: 'القرار قبل إدخال البيانات', body: 'يتولى الذكاء الاصطناعي الأعمال المتكررة ويحوّل المعلومات التشغيلية إلى سياق عملي.' },
      { number: '03', title: 'مساحة عمل مترابطة', body: 'تبقى الأعمال التجارية والتشغيلية والامتثال والمالية متوافقة عبر كل رحلة.' },
    ],
    cta: 'تحدث مع فريقنا',
  },
};

function About() {
  const { language } = useLanguage();
  const t = COPY[language] || COPY.en;

  return (
    <section id="about" className="ed-about" aria-labelledby="about-title">
      <div className="ed-container">
        <div className="ed-about-heading">
          <div>
            <div className="ed-about-eyebrow">
              <span aria-hidden="true" />
              {t.eyebrow}
            </div>
            <h2 id="about-title">{t.title}</h2>
          </div>

          <div className="ed-about-story">
            <p className="ed-about-intro">{t.intro}</p>
            <p>{t.detail}</p>
            <div className="ed-about-signature" aria-label={`${t.marker} · ${t.product}`}>
              <span>{t.marker}</span>
              <span aria-hidden="true" className="ed-about-signature-dot" />
              <strong>{t.product}</strong>
            </div>
          </div>
        </div>

        <div className="ed-about-pillars">
          {t.pillars.map((pillar) => (
            <article className="ed-about-pillar" key={pillar.number}>
              <span className="ed-about-number">{pillar.number}</span>
              <h3>{pillar.title}</h3>
              <p>{pillar.body}</p>
            </article>
          ))}
        </div>

        <a className="ed-about-cta" href="mailto:service@fisfleetgo.com">
          <span>{t.cta}</span>
          <ArrowForwardRoundedIcon aria-hidden="true" />
        </a>
      </div>
    </section>
  );
}

export default About;
